import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, Navigate, NavLink, useNavigate } from "react-router-dom";
import { StatusPanel } from "../components/StatusPanel";
import { TaskExecutionPanel } from "../components/TaskExecutionPanel";
import { startQaFromOffer } from "../lib/adminQaService";
import { payoutModelLabel } from "../lib/compensation";
import { formatOfferDeadline } from "../lib/deadlines";
import { ROUTES, xpProgress } from "../lib/gameEngine";
import {
  getMemberProfile,
  isFixtureMode,
  listNotifications,
  listPendingReviews,
  submitReviewResult,
} from "../lib/memberService";
import { supabase } from "../lib/supabase";
import {
  acceptTaskOffer,
  getActiveTask,
  getEarningSummary,
  listTaskOffers,
  releaseTask,
} from "../lib/workflowService";
import type { MemberProfile } from "../lib/types";
import type { TaskOffer } from "../lib/workflowTypes";
import "../member-final.css";

export function DashboardPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [selectedOfferId, setSelectedOfferId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<"offers" | "active" | "reviews" | "alerts">("offers");
  const [reviewNotes, setReviewNotes] = useState("");
  const [activeReviewId, setActiveReviewId] = useState<string | null>(null);

  const profile = useQuery({ queryKey: ["profile"], queryFn: getMemberProfile });
  const offers = useQuery({ queryKey: ["task-offers"], queryFn: listTaskOffers });
  const activeTask = useQuery({ queryKey: ["active-task"], queryFn: getActiveTask });
  const earnings = useQuery({ queryKey: ["earning-summary"], queryFn: getEarningSummary });
  const notifications = useQuery({ queryKey: ["notifications"], queryFn: listNotifications });
  const canReview = Boolean(profile.data && (profile.data.level >= 6 || profile.data.role === "admin" || profile.data.role === "reviewer"));
  const reviews = useQuery({
    queryKey: ["pending-reviews"],
    enabled: canReview,
    queryFn: listPendingReviews,
  });

  const refreshTasks = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["task-offers"] }),
      queryClient.invalidateQueries({ queryKey: ["active-task"] }),
      queryClient.invalidateQueries({ queryKey: ["earning-summary"] }),
    ]);
  };
  const accept = useMutation({
    mutationFn: acceptTaskOffer,
    onSuccess: async () => {
      setSelectedOfferId(null);
      setActiveTab("active");
      await refreshTasks();
    },
  });
  const release = useMutation({ mutationFn: releaseTask, onSuccess: refreshTasks });
  const qaClaim = useMutation({
    mutationFn: startQaFromOffer,
    onSuccess: (run) => {
      setSelectedOfferId(null);
      navigate(`/admin/qa?run=${run.id}`);
    },
  });
  const review = useMutation({
    mutationFn: ({ id, decision }: { id: string; decision: "approved" | "rejected" }) => submitReviewResult(id, decision, reviewNotes),
    onSuccess: async () => {
      setActiveReviewId(null);
      setReviewNotes("");
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["pending-reviews"] }),
        queryClient.invalidateQueries({ queryKey: ["profile"] }),
      ]);
    },
  });

  async function logout() {
    if (!isFixtureMode) await supabase?.auth.signOut();
    navigate("/");
  }

  if (profile.isLoading) return <main className="house-loading"><div className="house-loading-mark">H</div><div className="house-loading-track"><i /></div><span>Opening The House…</span></main>;
  if (!profile.data) return <Navigate to="/login" replace />;
  if (profile.data.activeRoute === "unplaced") return <Navigate to="/app/proving" replace />;

  const progress = xpProgress(profile.data.xp);
  const selectedOffer = (offers.data ?? []).find((item) => item.id === selectedOfferId);
  const activeReview = (reviews.data ?? []).find((item) => item.id === activeReviewId);
  const route = ROUTES.find((item) => item.id === profile.data.activeRoute);
  const isAppAdmin = profile.data.role === "admin";
  const matchedCount = (offers.data ?? []).filter((item) => item.matched).length;
  const blockingTask = activeTask.data && hasBlockingAssignment(activeTask.data.state) ? activeTask.data : null;
  const firstQaOffer = (offers.data ?? [])[0];
  const visibleOffers = [...(offers.data ?? [])].sort((a, b) => Number(!canClaimOffer(a, profile.data, blockingTask?.state)) - Number(!canClaimOffer(b, profile.data, blockingTask?.state)));

  return (
    <div className="member-app">
      <header className="member-header">
        <Link className="member-logo" to="/app"><span>H</span><b>THE HOUSE</b></Link>
        <div className="member-header-actions">
          <NavLink to="/app/training">TRAINING</NavLink>
          <NavLink to="/app/earnings">EARNINGS</NavLink>
          {profile.data.level >= 7 && <NavLink to="/app/team">TEAM</NavLink>}
          {isAppAdmin && <NavLink to="/admin/qa">QA CONSOLE</NavLink>}
          <button onClick={logout}>{profile.data.handle}</button>
        </div>
      </header>

      <aside className="member-rail">
        <button className={activeTab === "offers" ? "active" : ""} onClick={() => setActiveTab("offers")}><span>⌂</span>Matched{matchedCount > 0 && <b>{matchedCount}</b>}</button>
        <button className={activeTab === "active" ? "active" : ""} onClick={() => setActiveTab("active")}><span>▶</span>Active</button>
        {canReview && <button className={activeTab === "reviews" ? "active" : ""} onClick={() => setActiveTab("reviews")}><span>✓</span>Review{Boolean(reviews.data?.length) && <b>{reviews.data?.length}</b>}</button>}
        <button className={activeTab === "alerts" ? "active" : ""} onClick={() => setActiveTab("alerts")}><span>●</span>Alerts</button>
        <NavLink to="/app/leaderboard"><span>↑</span>Board</NavLink>
        <NavLink to="/app/proving"><span>◇</span>Progress</NavLink>
        {profile.data.level >= 7 && <NavLink to="/app/team"><span>▦</span>Team</NavLink>}
      </aside>

      <main className="member-content">
        <section className="member-welcome">
          <div>
            <span className="member-kicker">LEVEL {profile.data.level} · {progress.current.title.toUpperCase()}</span>
            <h1>Make your next move.</h1>
            <p>{route?.description} Promote ready-made products, follow the SOP, and get paid for approved proof.</p>
          </div>
          <div className="member-balance">
            <span>AVAILABLE TO PAY OUT</span>
            <strong>${(earnings.data?.availableUsd ?? 0).toFixed(2)}</strong>
            <Link to="/app/earnings">OPEN EARNINGS →</Link>
          </div>
        </section>

        <section className="member-progress">
          <div><span>XP</span><b>{profile.data.xp.toLocaleString()}</b></div>
          <div className="member-progress-track"><i style={{ width: `${progress.percent}%` }} /></div>
          <div><span>TRUST</span><b>{profile.data.trustScore}</b></div>
        </section>

        {(accept.isError || release.isError || review.isError || qaClaim.isError) && (
          <StatusPanel title="Action blocked" message={(accept.error ?? release.error ?? review.error ?? qaClaim.error)?.message ?? "Try again."} kind="error" />
        )}
        {!isAppAdmin && (
          <StatusPanel
            title="QA claim hidden"
            message={`Your app role is ${profile.data.role}. QA claims require the app admin role in Supabase member_roles; Discord server roles do not unlock this button by themselves.`}
          />
        )}
        {isAppAdmin && (
          <section className="admin-qa-strip">
            <div>
              <span>ADMIN QA MODE</span>
              <b>Test any task without becoming a real worker.</b>
              <p>Normal accepts still follow worker gates. QA claims bypass the gates, use the same SOP/proof UI, and can never create withdrawable earnings.</p>
              {blockingTask && <small>Normal accepting is blocked because your account has a {blockingTask.state.replace("_", " ")} task: {blockingTask.title}.</small>}
            </div>
            <div>
              {firstQaOffer && (
                <button disabled={qaClaim.isPending} onClick={() => qaClaim.mutate(firstQaOffer.id)}>
                  {qaClaim.isPending ? "STARTING QA..." : "QA CLAIM FIRST TASK"}
                </button>
              )}
              <Link to="/admin/qa">OPEN QA CONSOLE</Link>
            </div>
          </section>
        )}

        {activeTab === "offers" && (
          <section className="member-section">
            <div className="member-section-head">
              <div><span>REPEATABLE TASKS</span><h2>Your board</h2></div>
              <p>Your route opens first. Other boards are visible so you understand the business, but they may be browse-only.</p>
            </div>
            <div className="offer-grid">
              {visibleOffers.map((offer) => {
                const blockedReason = taskBlockReason(offer, profile.data, blockingTask?.state);
                return (
                <article className={`offer-card ${offer.matched ? "matched" : ""} ${blockedReason ? "locked" : ""}`} key={offer.id}>
                  <div className="offer-card-top">
                    <span>{blockedReason ? "BROWSE ONLY" : offer.matched ? "READY FOR YOU" : `${offer.route.toUpperCase()} BOARD`}</span>
                    <b>${offer.rewardUsd}</b>
                  </div>
                  <h3>{offer.title}</h3>
                  <div className="offer-product"><span>{offer.product.productName}</span><small>{offer.product.targetCustomer}</small></div>
                  {offer.controlledRun && (
                    <div className="controlled-run-pill">
                      <span>{controlledRunLabel(offer)}</span>
                      <small>{offer.controlledRun.reviewMode?.replace("_", " ") ?? "review gated"} · {offer.controlledRun.auditSamplePercent ?? 20}% audit</small>
                    </div>
                  )}
                  <p>{offer.summary}</p>
                  {blockedReason && <small className="offer-lock-note">{blockedReason}</small>}
                  <dl>
                    <div><dt>TIME</dt><dd>~{offer.estimatedMinutes} min</dd></div>
                    <div><dt>{offer.offerExpiresAt ? "EXPIRES" : "DUE"}</dt><dd>{formatOfferDeadline(offer)}</dd></div>
                    <div><dt>PAYOUT</dt><dd>{offer.compensation.type.replace("_", " ")}</dd></div>
                  </dl>
                  <div className="offer-card-actions">
                    <button onClick={() => setSelectedOfferId(offer.id)}>VIEW TASK</button>
                    {isAppAdmin && (
                      <button className="qa-claim-button" disabled={qaClaim.isPending} onClick={() => qaClaim.mutate(offer.id)}>
                        {qaClaim.isPending ? "STARTING QA..." : "QA CLAIM"}
                      </button>
                    )}
                  </div>
                </article>
                );
              })}
            </div>
            {!offers.isLoading && !offers.data?.length && <Empty title="No ready offers." text="The engine is matching new work to your route." />}
          </section>
        )}

        {activeTab === "active" && (
          <section className="member-section">
            <div className="member-section-head">
              <div><span>AUTOSAVED SOP</span><h2>Active task</h2></div>
            </div>
            {activeTask.data ? (
              <TaskExecutionPanel
                task={activeTask.data}
                onChanged={() => queryClient.invalidateQueries({ queryKey: ["active-task"] })}
                onSubmitted={refreshTasks}
                onReleased={() => release.mutate(activeTask.data!.assignmentId)}
                busy={release.isPending}
              />
            ) : <Empty title="Nothing active." text="Accept an offer when you are ready to execute it." action="SEE OFFERS" onAction={() => setActiveTab("offers")} />}
          </section>
        )}

        {activeTab === "reviews" && canReview && (
          <section className="member-section">
            <div className="member-section-head"><div><span>{profile.data.level >= 6 ? "LEVEL 6 ACCESS" : "ADMIN ACCESS"}</span><h2>Review queue</h2></div><p>Judge the proof against the frozen SOP. Approval is what creates payable earnings.</p></div>
            <div className="review-layout">
              <div className="review-list">
                {(reviews.data ?? []).map((item) => <button className={item.id === activeReviewId ? "active" : ""} key={item.id} onClick={() => setActiveReviewId(item.id)}><b>{item.brand}</b><span>{new Date(item.createdAt).toLocaleString()}</span></button>)}
              </div>
              <div className="review-detail">
                {activeReview ? (
                  <>
                    <span className="member-kicker">SUBMITTED EVIDENCE</span>
                    <h3>{activeReview.brand}</h3>
                    <p>{activeReview.evidenceNote}</p>
                    <ul>{activeReview.sopChecklist.map((item) => <li key={item}>{item}</li>)}</ul>
                    <label><span>Reviewer notes</span><textarea value={reviewNotes} onChange={(event) => setReviewNotes(event.target.value)} /></label>
                    <div className="member-actions">
                      <button className="primary" disabled={review.isPending} onClick={() => review.mutate({ id: activeReview.id, decision: "approved" })}>APPROVE</button>
                      <button className="danger" disabled={review.isPending || reviewNotes.trim().length < 10} onClick={() => review.mutate({ id: activeReview.id, decision: "rejected" })}>REQUEST CHANGES</button>
                    </div>
                  </>
                ) : <Empty title="Select a submission." text="The correct answer is never shown before your decision." />}
              </div>
            </div>
          </section>
        )}

        {activeTab === "alerts" && (
          <section className="member-section">
            <div className="member-section-head"><div><span>ENGINE ACTIVITY</span><h2>Alerts</h2></div></div>
            <div className="alert-list">{(notifications.data ?? []).map((item) => <article key={item.id}><div><b>{item.title}</b><p>{item.message}</p></div><time>{new Date(item.createdAt).toLocaleString()}</time></article>)}</div>
          </section>
        )}
      </main>

      {selectedOffer && (
        <div className="mission-modal" role="dialog" aria-modal="true" aria-labelledby="task-title">
          <button className="mission-backdrop" aria-label="Close task" onClick={() => setSelectedOfferId(null)} />
          <section>
            <button className="mission-close" onClick={() => setSelectedOfferId(null)}>×</button>
            <span className="member-kicker">{selectedOffer.route.toUpperCase()} · ${selectedOffer.rewardUsd}</span>
            <h2 id="task-title">{selectedOffer.title}</h2>
            <p>{selectedOffer.summary}</p>
            {taskBlockReason(selectedOffer, profile.data, blockingTask?.state) && <StatusPanel title="Browse-only task" message={taskBlockReason(selectedOffer, profile.data, blockingTask?.state) ?? ""} />}
            <div className="mission-product">
              <div><span>PRODUCT</span><b>{selectedOffer.product.productName}</b><small>{selectedOffer.product.offer}</small></div>
              <div><span>TARGET</span><b>{selectedOffer.product.targetCustomer}</b><small>{selectedOffer.product.objective}</small></div>
            </div>
            <div className="mission-preview-grid">
              <div><span>ESTIMATED TIME</span><b>{selectedOffer.estimatedMinutes} minutes</b></div>
              <div><span>{selectedOffer.offerExpiresAt ? "OFFER EXPIRES" : "DUE"}</span><b>{selectedOffer.offerExpiresAt ? formatOfferDeadline(selectedOffer) : new Date(selectedOffer.dueAt).toLocaleString()}</b></div>
              <div><span>PAYOUT MODEL</span><b>{payoutModelLabel(selectedOffer.compensation, selectedOffer.rewardUsd)}</b></div>
              <div><span>CELL</span><b>{selectedOffer.cellName ?? "Direct board"}</b><small>{selectedOffer.managerHandle ? `Manager ${selectedOffer.managerHandle}` : "No manager override"}</small></div>
              {selectedOffer.controlledRun && (
                <>
                  <div><span>CONTROLLED RUN</span><b>{controlledRunLabel(selectedOffer)}</b><small>Budget ${selectedOffer.controlledRun.dailyBudgetUsd ?? selectedOffer.rewardUsd}/day</small></div>
                  <div><span>AUTOMATION</span><b>{selectedOffer.controlledRun.reviewMode?.replace("_", " ") ?? "review gated"}</b><small>{selectedOffer.controlledRun.riskLevel ?? "medium"} risk · {selectedOffer.controlledRun.auditSamplePercent ?? 20}% audit</small></div>
                </>
              )}
            </div>
            <h3>What you will do</h3>
            <ol>{selectedOffer.steps.map((step) => <li key={step.key}><b>{step.title}</b><span>{step.instruction}</span></li>)}</ol>
            <h3>Proof required</h3>
            <ul>{selectedOffer.evidenceRequirements.map((item) => <li key={item.key}>{item.label}</li>)}</ul>
            <div className="member-actions">
              <button className="primary" disabled={accept.isPending || Boolean(taskBlockReason(selectedOffer, profile.data, blockingTask?.state))} onClick={() => accept.mutate(selectedOffer.id)}>ACCEPT TASK</button>
              {isAppAdmin ? (
                <button className="qa-claim-button" disabled={qaClaim.isPending} onClick={() => qaClaim.mutate(selectedOffer.id)}>
                  {qaClaim.isPending ? "STARTING QA..." : "QA CLAIM AS WORKER"}
                </button>
              ) : (
                <span className="qa-role-note">QA requires app admin role. Current app role: {profile.data.role}.</span>
              )}
              <button onClick={() => setSelectedOfferId(null)}>NOT NOW</button>
            </div>
          </section>
        </div>
      )}

      <nav className="member-mobile-nav">
        <button className={activeTab === "offers" ? "active" : ""} onClick={() => setActiveTab("offers")}>Matched{matchedCount > 0 ? ` ${matchedCount}` : ""}</button>
        <button className={activeTab === "active" ? "active" : ""} onClick={() => setActiveTab("active")}>Active</button>
        <NavLink to="/app/training">Training</NavLink>
        <NavLink to="/app/earnings">Earnings</NavLink>
        {profile.data.level >= 7 && <NavLink to="/app/team">Team</NavLink>}
      </nav>
    </div>
  );
}

function canClaimOffer(offer: TaskOffer, profile: MemberProfile, blockingState?: string) {
  return !taskBlockReason(offer, profile, blockingState);
}

function taskBlockReason(offer: TaskOffer, profile: MemberProfile, blockingState?: string) {
  if (blockingState) return `You already have a ${blockingState.replace("_", " ")} task. Finish the review cycle or use QA CLAIM as admin.`;
  if (profile.activeRoute !== offer.route) return `Your route is ${profile.activeRoute.toUpperCase()}. This task is for ${offer.route.toUpperCase()}.`;
  if (offer.minimumLevel != null && profile.level < offer.minimumLevel) return `Unlocks at Level ${offer.minimumLevel}.`;
  if (offer.minimumTrust != null && profile.trustScore < offer.minimumTrust) return `Requires ${offer.minimumTrust}+ trust.`;
  if (profile.membershipStatus && profile.membershipStatus !== "active") return "Membership must be active.";
  if (profile.legalApproved === false) return "Legal approval is required.";
  if (profile.payoutEligible === false) return "Payout eligibility is required.";
  return "";
}

function hasBlockingAssignment(state: string) {
  return ["offered", "active", "submitted", "changes_requested"].includes(state);
}

function controlledRunLabel(offer: TaskOffer) {
  const type = offer.controlledRun?.runType ?? (offer.route === "closer" ? "demo_booking" : "lead_finder");
  return type.replaceAll("_", " ").replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function Empty({ title, text, action, onAction }: { title: string; text: string; action?: string; onAction?: () => void }) {
  return <div className="member-empty"><span>◇</span><h3>{title}</h3><p>{text}</p>{action && <button onClick={onAction}>{action}</button>}</div>;
}
